import { CartAddEvent } from '@theme/events';

const ADD_SELECTOR = '[data-variant-list-add]';
const IMAGE_SELECTOR = '[data-variant-image-open]';
const OPEN_DIALOG_SELECTOR = '[data-variant-image-dialog]';

let imageHistoryState = false;

function sortVariantList(list) {
  const rows = [...list.children].filter((child) => child.matches('.variant-list__row'));
  const sorted = [...rows].sort((a, b) => {
    const aAvailable = a.dataset.variantAvailable === 'true';
    const bAvailable = b.dataset.variantAvailable === 'true';
    return Number(bAvailable) - Number(aAvailable);
  });

  if (sorted.some((row, index) => row !== rows[index])) {
    list.append(...sorted);
  }
}

function sortVariantLists(root = document) {
  if (root.matches?.('.variant-list')) sortVariantList(root);
  root.querySelectorAll?.('.variant-list').forEach(sortVariantList);
}

function closeImageDialog(dialog, restoreHistory = true) {
  if (!dialog?.open) return;

  dialog.close();
  if (restoreHistory && imageHistoryState) {
    imageHistoryState = false;
    history.back();
  }
}

function openImageDialog(trigger) {
  const dialog = trigger.closest('variant-picker')?.querySelector(OPEN_DIALOG_SELECTOR);
  const image = dialog?.querySelector('[data-variant-image-dialog-image]');
  const caption = dialog?.querySelector('[data-variant-image-dialog-caption]');
  const source = trigger.dataset.imageSrc;

  if (!dialog || !image || !source) return;

  image.src = source;
  image.alt = trigger.dataset.imageAlt || '';
  if (caption) caption.textContent = trigger.dataset.imageAlt || '';
  dialog.showModal();

  if (!imageHistoryState) {
    history.pushState({ variantImage: true }, '');
    imageHistoryState = true;
  }
}

function productFormFor(variantPicker) {
  const productId = variantPicker.dataset.productId;
  return document.querySelector(`product-form-component[data-product-id="${productId}"]`);
}

async function addVariant(button) {
  const variantPicker = button.closest('variant-picker');
  const productForm = variantPicker && productFormFor(variantPicker);
  const variantId = button.dataset.variantId;
  const form = productForm?.querySelector('form[data-type="add-to-cart-form"]');

  if (!productForm || !form || !variantId || button.disabled) return;

  const originalText = button.textContent;
  button.disabled = true;
  button.textContent = 'Adding…';

  const formData = new FormData(form);
  formData.set('id', variantId);

  const cartItems = document.querySelectorAll('cart-items-component[data-section-id]');
  const sectionIds = [...cartItems].map((item) => item.dataset.sectionId).filter(Boolean);
  if (sectionIds.length) formData.set('sections', sectionIds.join(','));

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    });
    const result = await response.json();

    if (result.status) {
      button.textContent = 'Unavailable';
      return;
    }

    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();
    document.dispatchEvent(
      new CartAddEvent(cart, productForm.id, {
        source: 'variant-list',
        variantId,
        productId: variantPicker.dataset.productId,
        sections: result.sections,
      })
    );
      button.textContent = 'Added';
  } catch (error) {
    console.error('Variant add to cart failed', error);
    button.textContent = 'Try again';
  } finally {
    window.setTimeout(() => {
      button.disabled = false;
      button.textContent = originalText;
    }, 1500);
  }
}

document.addEventListener('click', (event) => {
  const row = event.target.closest?.('[data-variant-image-row]');
  const addButton = event.target.closest?.(ADD_SELECTOR);
  if (row && !addButton) {
    const imageTrigger = row.querySelector(IMAGE_SELECTOR);
    if (imageTrigger?.dataset.imageSrc) {
      event.preventDefault();
      openImageDialog(imageTrigger);
      return;
    }
  }

  const imageTrigger = event.target.closest?.(IMAGE_SELECTOR);
  if (imageTrigger) {
    event.preventDefault();
    openImageDialog(imageTrigger);
    return;
  }

  const closeButton = event.target.closest?.('[data-variant-image-close]');
  if (closeButton) {
    closeImageDialog(closeButton.closest(OPEN_DIALOG_SELECTOR));
    return;
  }

  const button = event.target.closest?.(ADD_SELECTOR);
  if (!button) return;

  event.preventDefault();
  event.stopPropagation();
  addVariant(button);
});

document.addEventListener('keydown', (event) => {
  const row = event.target.closest?.('[data-variant-image-row]');
  const addButton = event.target.closest?.(ADD_SELECTOR);
  if (row && !addButton && (event.key === 'Enter' || event.key === ' ')) {
    const imageTrigger = row.querySelector(IMAGE_SELECTOR);
    if (imageTrigger?.dataset.imageSrc) {
      event.preventDefault();
      openImageDialog(imageTrigger);
      return;
    }
  }

  const imageTrigger = event.target.closest?.(IMAGE_SELECTOR);
  if (!imageTrigger || (event.key !== 'Enter' && event.key !== ' ')) return;

  event.preventDefault();
  openImageDialog(imageTrigger);
});

document.addEventListener('cancel', (event) => {
  const dialog = event.target.closest?.(OPEN_DIALOG_SELECTOR);
  if (!dialog) return;

  event.preventDefault();
  closeImageDialog(dialog);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => sortVariantLists(), { once: true });
} else {
  sortVariantLists();
}

const variantListObserver = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node.nodeType !== Node.ELEMENT_NODE) continue;
      sortVariantLists(node);
      node.closest?.('.variant-list') && sortVariantList(node.closest('.variant-list'));
    }
  }
});

variantListObserver.observe(document.body, { childList: true, subtree: true });

window.addEventListener('popstate', () => {
  const dialog = document.querySelector(OPEN_DIALOG_SELECTOR);
  if (!dialog?.open || !imageHistoryState) return;

  imageHistoryState = false;
  closeImageDialog(dialog, false);
});
